import { z } from 'zod';

import { ZodCreateUserResponse } from '@Common/types/auth/create_user';
import { ZodPinCode } from '@Common/types/auth/reset_pin';
import { ZodLanguageCode } from '@Common/types/language';
import { ZodPappersLegalInfo } from '@Common/types/legal_representatives';
import { ZodRawUBODetails } from '@Common/types/questions/company';
import { ZodAddressInfo } from '@Common/types/questions/user_profile';
import { ZodCity } from '@Common/types/user/address';
import { ZodUserBirthdate } from '@Common/types/user/birthdate';
import { ZodUserFirstName, ZodUserLastName } from '@Common/types/user/legal_name';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { ZodDateString } from '@Common/types/zod/zod-date-string';
import { ZodAppUserEmail } from '@Common/types/zod/zod-email';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';
import { ZodUsername } from '@Common/types/zod/zod-username';

export const SUPPORTED_LEGAL_CATEGORY_CODES = ['5410', '5498', '5499', '5710', '5720'];

export const ZodCreateBusinessUserAccountHolder = z.object({
  firstname: ZodUserFirstName,
  lastname: ZodUserLastName,
  birthdate: ZodUserBirthdate,
  pin_code: ZodPinCode,
  phone_number: ZodPhoneNumber,
  email: ZodAppUserEmail,
  email_marketing_consent: z.boolean(),
  confirmation_code: ZodTrimmedString,
  device_id: ZodTrimmedString,
  language: ZodLanguageCode,
  city: ZodCity,
  country_of_tax_residence: z.enum(['FR']),
  citizenship: ZodCountryCode,
  username: ZodUsername.optional(),
  conflict_of_interest_policy_agreement: z.literal(true).optional(),
});

const ZodCreateBusinessUserCompanyDirector = z.object({
  firstname: ZodTrimmedString,
  lastname: ZodTrimmedString,
  position: ZodTrimmedString,
  legal_info: ZodPappersLegalInfo.optional(),
});

export const ZodCreateBusinessUserCompanyDirectors = z.array(ZodCreateBusinessUserCompanyDirector);
export type CreateBusinessUserCompanyDirectors = z.infer<
  typeof ZodCreateBusinessUserCompanyDirectors
>;

export const ZodCreateBusinessUserCompanyUBOs = z.array(ZodRawUBODetails);
export type CreateBusinessUserCompanyUBOs = z.infer<typeof ZodCreateBusinessUserCompanyUBOs>;

export type CompanyIndustryType =
  | 'accommodation_and_food_services'
  | 'accountancy_services'
  | 'administrative_and_support_services'
  | 'adult_entertainment'
  | 'agriculture'
  | 'arms_defense_and_private_security'
  | 'art_entertainment_and_recreation'
  | 'auctions'
  | 'cbd_business'
  | 'construction'
  | 'education'
  | 'energy'
  | 'financial_and_insurance_services'
  | 'gambling_casinos_and_online_betting'
  | 'health_medicine_and_social_care'
  | 'human_resources_services'
  | 'information_and_communication'
  | 'legal_and_consulting'
  | 'manufacturing'
  | 'merchant_services'
  | 'mining_and_oil_activities'
  | 'precious_stones_and_metals'
  | 'professional_scientific_and_technical_services'
  | 'property'
  | 'public_sector'
  | 'real_estate_agency_and_renting'
  | 'remittance_and_money_service_business'
  | 'retail_services'
  | 'sects_cults_and_religion'
  | 'supply_chain_management'
  | 'technology_services'
  | 'tourism_and_travel'
  | 'trading_unregulated_financial_and_crypto_services'
  | 'transport_storage_and_logistics'
  | 'trust_charities_and_foundations'
  | 'vehicle_trade_and_repair'
  | 'water_supply_sewerage_and_waste_management'
  | 'asset_holding_company';

export const CompanyIndustryValues: ReadonlyArray<CompanyIndustryType> = [
  'accommodation_and_food_services',
  'accountancy_services',
  'administrative_and_support_services',
  'adult_entertainment',
  'agriculture',
  'arms_defense_and_private_security',
  'art_entertainment_and_recreation',
  'auctions',
  'cbd_business',
  'construction',
  'education',
  'energy',
  'financial_and_insurance_services',
  'gambling_casinos_and_online_betting',
  'health_medicine_and_social_care',
  'human_resources_services',
  'information_and_communication',
  'legal_and_consulting',
  'manufacturing',
  'merchant_services',
  'mining_and_oil_activities',
  'precious_stones_and_metals',
  'professional_scientific_and_technical_services',
  'property',
  'public_sector',
  'real_estate_agency_and_renting',
  'remittance_and_money_service_business',
  'retail_services',
  'sects_cults_and_religion',
  'supply_chain_management',
  'technology_services',
  'tourism_and_travel',
  'trading_unregulated_financial_and_crypto_services',
  'transport_storage_and_logistics',
  'trust_charities_and_foundations',
  'vehicle_trade_and_repair',
  'water_supply_sewerage_and_waste_management',
  'asset_holding_company',
];

export const ZodCompanyIndustry = z.enum([CompanyIndustryValues[0], ...CompanyIndustryValues]);

export const ZodCreateBusinessUserCompany = z.object({
  name: ZodTrimmedString,
  invest_index: z.number().int(),
  revenue_index: z.number().int(),
  legal_form: ZodTrimmedString,
  legal_category: ZodTrimmedString.optional(),
  industry: ZodCompanyIndustry,
  siret: ZodTrimmedString.length(14),
  siren: ZodTrimmedString.length(9),
  registration_date: ZodDateString,
  naf_code: ZodTrimmedString,
  address: ZodAddressInfo,
  mailing_address: ZodAddressInfo.optional(),
  directors: ZodCreateBusinessUserCompanyDirectors,
  ubos: z.object({
    is_confirmed: z.boolean(),
    items: ZodCreateBusinessUserCompanyUBOs,
  }),
});

export type CreateBusinessUserCompanyPayload = z.infer<typeof ZodCreateBusinessUserCompany>;

export const ZodCreateBusinessUserSourceOfFunds = z.object({
  type: z.enum(['tax_statement', 'bank_statement']),
  document_files: z.array(
    z.object({
      name: ZodTrimmedString,
      path: ZodTrimmedString,
    }),
  ),
});

export type CreateBusinessUserSourceOfFunds = z.infer<typeof ZodCreateBusinessUserSourceOfFunds>;

export const ZodCreateBusinessUserBody = z
  .object({
    account_holder: ZodCreateBusinessUserAccountHolder,
    source_of_funds: ZodCreateBusinessUserSourceOfFunds,
    company: ZodCreateBusinessUserCompany,
  })
  .refine((data) => data.company.siren === data.company.siret.substring(0, 9), {
    message: 'SIREN must be the first 9 characters of SIRET',
    path: ['company.siren'],
  });

export const postAuthCreateBusinessUserEndpoint = endpoint({
  method: 'POST',
  body: ZodCreateBusinessUserBody,
  responseBody: ZodCreateUserResponse,
});

export type PostAuthCreateBusinessUserEndpoint = GetEndpointType<
  typeof postAuthCreateBusinessUserEndpoint
>;
